import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, imgUrl } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const ClipRow = ({ clip, products, onChanged, onDeleted }) => {
  const [c, setC] = useState(clip);
  useEffect(() => setC(clip), [clip]);
  const save = async () => {
    try {
      const { data } = await api.put(`/admin/clips/${c.id}`, { title: c.title, active: c.active, order: Number(c.order) || 0, product_slug: c.product_slug || null });
      onChanged(data);
      toast.success("Clip saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    if (!window.confirm("Remove this clip from the site?")) return;
    try {
      await api.delete(`/admin/clips/${c.id}`);
      onDeleted(c.id);
      toast.success("Clip removed");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <li data-testid={ADMIN.clipRow(c.id)} className="py-4 grid grid-cols-[96px_1fr] md:grid-cols-[120px_1fr_auto] gap-4 items-center">
      <div className="w-24 md:w-[120px] aspect-[3/2] rounded-xl bg-[#1C1917] overflow-hidden grid place-items-center">
        {c.status === "ready" ? (
          <video src={imgUrl(c.video)} poster={imgUrl(c.poster)} muted playsInline controls className="w-full h-full object-contain" />
        ) : (
          <span data-testid={ADMIN.clipStatus(c.id)} className="text-[11px] text-white/80 flex items-center gap-1">
            {c.status === "processing" ? <><Loader2 size={12} className="animate-spin" /> Preparing…</> : "Failed"}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        <input data-testid={ADMIN.clipTitle(c.id)} value={c.title} onChange={(e) => setC({ ...c, title: e.target.value })} placeholder="Title (optional)" className="flex-1 min-w-[140px] px-3 py-2 rounded-xl border border-[#E7E0D3] bg-white text-sm" />
        <label className="flex items-center gap-1.5 text-sm">Order <input data-testid={ADMIN.clipOrder(c.id)} type="number" value={c.order} onChange={(e) => setC({ ...c, order: e.target.value })} className="w-16 px-2 py-1.5 rounded-lg border border-[#E7E0D3] bg-white" /></label>
        <select data-testid={ADMIN.clipProduct(c.id)} value={c.product_slug || ""} onChange={(e) => setC({ ...c, product_slug: e.target.value })} aria-label="Toy in this clip" className="w-full sm:w-auto sm:min-w-[220px] px-3 py-2 rounded-xl border border-[#E7E0D3] bg-white text-sm">
          <option value="">No toy tagged</option>
          {products.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-sm"><input data-testid={ADMIN.clipActive(c.id)} type="checkbox" checked={c.active} onChange={(e) => setC({ ...c, active: e.target.checked })} /> Show on site</label>
        {c.status === "failed" && <span className="text-xs text-[#B42318]">This video couldn't be converted. Please try a different file.</span>}
      </div>
      <div className="col-span-2 md:col-span-1 flex gap-2">
        <button data-testid={ADMIN.clipSave(c.id)} onClick={save} className="px-4 py-2 rounded-full bg-[#1C1917] text-white text-sm hover:bg-[#E06D44] transition-colors">Save</button>
        <button data-testid={ADMIN.clipDelete(c.id)} onClick={remove} aria-label="Remove clip" className="w-9 h-9 rounded-full border border-[#E7E0D3] grid place-items-center hover:bg-[#FDECEC] transition-colors"><Trash2 size={15} /></button>
      </div>
    </li>
  );
};

export const AdminClips = () => {
  const [clips, setClips] = useState(null);
  const [products, setProducts] = useState([]);
  const [uploading, setUploading] = useState(0);
  const ref = useRef(null);
  const load = useCallback(() => api.get("/admin/clips").then(({ data }) => setClips(data)).catch(() => setClips([])), []);
  useEffect(() => {
    load();
    api.get("/products").then(({ data }) => setProducts([...data].sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
  }, [load]);
  useEffect(() => {
    if (!clips?.some((c) => c.status === "processing")) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [clips, load]);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setUploading(1);
    try {
      const { data } = await api.post("/admin/clips", form, {
        onUploadProgress: (p) => p.total && setUploading(Math.max(1, Math.round((p.loaded / p.total) * 100))),
      });
      setClips((l) => [data, ...(l || [])]);
      toast.success("Uploaded! Preparing the clip for the site. This takes about a minute.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(0);
    }
  };

  return (
    <section className="mt-6">
      <div className="bg-white border border-[#F0EBE1] rounded-[1.5rem] p-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-heading font-bold">Kiddee clips</p>
          <p className="text-sm text-stone-600 mt-1">These play one after another in the Meet Kiddee section, after her main clip. Tag the toy she's playing with to show a "Shop this toy" button on the video. Any phone video works. The site converts it so every browser can play it (up to 90 seconds are kept).</p>
        </div>
        <button data-testid={ADMIN.clipUpload} onClick={() => ref.current?.click()} disabled={!!uploading} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#E06D44] text-white font-medium hover:bg-[#C95B33] disabled:opacity-60 transition-colors">
          {uploading ? <><Loader2 size={16} className="animate-spin" /> Uploading {uploading}%</> : <><Upload size={16} /> Upload a clip</>}
        </button>
        <input ref={ref} data-testid={ADMIN.clipUploadInput} type="file" accept="video/*" onChange={pick} className="hidden" />
      </div>
      {clips && clips.length === 0 && <p className="mt-6 text-stone-600">No extra clips yet. Upload one from your phone to add it to the rotation.</p>}
      <ul className="mt-4 divide-y divide-[#F0EBE1]">
        {(clips || []).map((c) => (
          <ClipRow key={c.id} clip={c} products={products} onChanged={(d) => setClips((l) => l.map((x) => (x.id === d.id ? d : x)))} onDeleted={(id) => setClips((l) => l.filter((x) => x.id !== id))} />
        ))}
      </ul>
    </section>
  );
};
